import { StyleSheet } from 'aphrodite'
import { Amplify, Auth } from 'aws-amplify'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import UIButton from '../../components/UI/UIButton'
import UIInput from '../../components/UI/UIInput'
import UIPassword from '../../components/UI/UIPassword'
import config from '../../config'
import PropTypes from 'prop-types'

const styles = StyleSheet.create({
  buttonSubmit: {
    marginTop: '30px',
  },
})

export default function ChangePasswordForm({ email, onPasswordChanged }) {
  Amplify.configure({
    Auth: config.aws,
  })

  const { t } = useTranslation('translation')
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({ mode: 'all' })

  const onSubmit = async (data) => {
    if (data.password !== data.password_confirmation)
      toast.error(
        t('pages.forgottenPassword.errorFormMessage.passwordNotCoresponding')
      )
    else {
      await toast.promise(
        Auth.forgotPasswordSubmit(email, data.code, data.password)
          .then(() => {
            toast.success(t('pages.forgottenPassword.changePasswordSuccess'))
            onPasswordChanged()
          })
          .catch(() =>
            toast.error(t('pages.forgottenPassword.codeNotCorresponding'))
          ),
        {
          pending: t('pages.forgottenPassword.pendingMessage'),
        }
      )
    }
  }
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="code"
        control={control}
        render={({ field }) => (
          <UIInput
            {...field}
            label={t('pages.forgottenPassword.formLabel.codeLabel')}
            placeholder={t('pages.forgottenPassword.formLabel.codeLabel')}
            errorMessage={errors.code && errors.code.message}
          />
        )}
        rules={{
          required: {
            value: true,
            message: t('pages.forgottenPassword.errorFormMessage.codeRequired'),
          },
        }}
      />
      <Controller
        name="password"
        control={control}
        render={({ field }) => (
          <UIPassword
            {...field}
            label={t('pages.forgottenPassword.formLabel.passwordLabel')}
            placeholder={`${t('common.form.password')}*`}
            errorMessage={errors.password && errors.password.message}
          />
        )}
        rules={{
          required: {
            value: true,
            message: t('common.form.errorMessage.passwordEmptyError'),
          },
          pattern: {
            value: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z\d]+).{8,}$/,
            message: t('common.form.errorMessage.passwordNotValidError'),
          },
          minLength: {
            value: '8',
            message: t('common.form.errorMessage.passwordMinLengthError'),
          },
        }}
      />
      <Controller
        name="password_confirmation"
        control={control}
        render={({ field }) => (
          <UIPassword
            {...field}
            label={t('pages.forgottenPassword.formLabel.confirmPassword')}
            placeholder={`${t(
              'pages.forgottenPassword.formLabel.confirmPassword'
            )}*`}
            errorMessage={
              errors.password_confirmation &&
              errors.password_confirmation.message
            }
          />
        )}
        rules={{
          required: {
            value: true,
            message: t('common.form.errorMessage.passwordEmptyError'),
          },
        }}
      />
      <UIButton
        type="submit"
        kind="button"
        design="primary"
        label={t('pages.forgottenPassword.formLabel.changePassword')}
        fullWidth
        dynamicStyle={styles.buttonSubmit}
      />
    </form>
  )
}

ChangePasswordForm.propTypes = {
  email: PropTypes.string,
  onPasswordChanged: PropTypes.func,
}
